/**
 * The Knowledge Base page's glossary and search, as data (ACOP-146; the scaffold from ACOP-100).
 *
 * Deliberately free of React so the rules can be unit-tested directly — the same reasoning as
 * utils/nav.ts and utils/kbArticleHtml.ts.
 *
 * The content is NOT in this repo. AVS OS builds the per-product glossary and the paged search from
 * the ingested sdlc-kb metadata (`products`, `category`, `subcategory`, `title`, `summary`) and
 * decides what each person may see; this file only describes the shape that comes back and builds
 * the two requests. The static, empty GLOSSARY this file used to hold is gone with its scaffolding.
 */

export const PAGE_SIZE = 20;

export type KbArticleRef = {
  id: string;
  title: string;
  summary: string | null;
  category: string;
  subcategory: string | null;
  urlName: string | null;
  /** In a non-public area: only ever returned to an employee who asked for it (Beta Preview on). */
  internal: boolean;
  products: string[];
};

export type GlossaryData = {
  /** `name` is the stored value sent back as `product=`; `label` is what a person reads (Core → AvSight). */
  products: Array<{ name: string; label?: string; count: number }>;
  product: string | null;
  groups: Array<{
    category: string;
    subcategories: Array<{ name: string | null; articles: KbArticleRef[] }>;
  }>;
  total: number;
};

export type SearchData = {
  q: string;
  product: string | null;
  page: number;
  pageSize: number;
  total: number;
  results: KbArticleRef[];
};

/**
 * The phrase as it should be searched: whitespace collapsed and trimmed. Empty means "no search",
 * i.e. show the glossary.
 */
export function normaliseQuery(raw: string): string {
  return (raw || "").replace(/\s+/g, " ").trim();
}

/**
 * Query parameters for the glossary request. `includeInternal` is only ever sent when the caller
 * asks for it (the Beta Preview toggle, employees); copilot-api forwards it for an employee and
 * drops it for anyone else, and AVS OS ignores it for a non-employee principal as well.
 */
export function glossaryParams(product: string | null, includeInternal: boolean): Record<string, string> {
  const params: Record<string, string> = {};
  if (product) params.product = product;
  if (includeInternal) params.includeInternal = "true";
  return params;
}

export function searchParams(query: string, page: number, includeInternal: boolean): Record<string, string> {
  const params: Record<string, string> = {
    q: normaliseQuery(query),
    page: String(Math.max(1, Math.floor(page) || 1)),
    pageSize: String(PAGE_SIZE),
  };
  if (includeInternal) params.includeInternal = "true";
  return params;
}

/** "Showing 21–40 of 45", the page count, and whether Previous / Next apply. */
export function pageInfo(total: number, page: number, pageSize: number) {
  const pages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const current = Math.min(Math.max(1, page), pages);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(total, current * pageSize);
  return { from, to, pages, current, hasPrev: current > 1, hasNext: current < pages };
}

/** "Category › Subcategory" for a result row; the category alone when there is no subcategory. */
export function topicLabel(a: Pick<KbArticleRef, "category" | "subcategory">): string {
  return a.subcategory ? `${a.category} › ${a.subcategory}` : a.category;
}

/** The value that opens an article. The id always resolves; a slug may not exist. */
export function articleKey(a: Pick<KbArticleRef, "id">): string {
  return a.id;
}

/** An internal article can be listed for an employee but has no viewer route yet — see KbBrowse. */
export function isOpenable(a: Pick<KbArticleRef, "internal">): boolean {
  return !a.internal;
}
